import React, { useState, useEffect } from "react";
import InputNumber from "./components/InputNumber";

type Equipamento = {
  nome: string;
  horas: number;
  consumoWatts: number;
  periodicidade: "dia" | "semana" | "mes";
};

const App: React.FC = () => {
  const [equipamentos, setEquipamentos] = useState<Equipamento[]>([]);
  const [nome, setNome] = useState("");
  const [horas, setHoras] = useState("");
  const [consumoWatts, setConsumoWatts] = useState("");
  const [periodicidade, setPeriodicidade] = useState<"dia" | "semana" | "mes">("dia");
  const [precoKwh, setPrecoKwh] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);

  useEffect(() => {
    const storedEquipamentos = localStorage.getItem("equipamentos");
    const storedPrecoKwh = localStorage.getItem("precoKwh");

    if (storedEquipamentos) {
      setEquipamentos(JSON.parse(storedEquipamentos));
    }

    if (storedPrecoKwh) {
      setPrecoKwh(storedPrecoKwh);
    }
  }, []);

  const salvarEquipamento = () => {
    if (!nome || Number(horas) <= 0 || Number(consumoWatts) <= 0) {
      alert("Preencha todos os campos corretamente!");
      return;
    }

    const equipamento: Equipamento = {
      nome,
      horas: Number(horas),
      consumoWatts: Number(consumoWatts),
      periodicidade,
    };

    if (editIndex !== null) {
      const novosEquipamentos = [...equipamentos];
      novosEquipamentos[editIndex] = equipamento;
      setEquipamentos(novosEquipamentos);
      localStorage.setItem("equipamentos", JSON.stringify(novosEquipamentos));
      setEditIndex(null);
    } else {
      const novosEquipamentos = [...equipamentos, equipamento];
      setEquipamentos(novosEquipamentos);
      localStorage.setItem("equipamentos", JSON.stringify(novosEquipamentos));
    }

    setNome("");
    setHoras("");
    setConsumoWatts("");
    setPeriodicidade("dia");
  };

  const editarEquipamento = (index: number) => {
    const equipamento = equipamentos[index];
    setNome(equipamento.nome);
    setHoras(equipamento.horas.toString(0));
    setConsumoWatts(equipamento.consumoWatts.toString());
    setPeriodicidade(equipamento.periodicidade);
    setEditIndex(index);
  };

  const excluirEquipamento = (index: number) => {
    const novosEquipamentos = equipamentos.filter((_, i) => i !== index);
    setEquipamentos(novosEquipamentos);
    localStorage.setItem("equipamentos", JSON.stringify(novosEquipamentos));
  };

  const salvarPrecoKwh = (valor: string) => {
    setPrecoKwh(valor);
    localStorage.setItem("precoKwh", valor.toString());
  };

  const calcularCustoMensal = (equipamento: Equipamento): number => {
    const horasMensais =
      equipamento.periodicidade === "dia"
        ? equipamento.horas * 30
        : equipamento.periodicidade === "semana"
          ? equipamento.horas * 4
          : equipamento.horas;

    const consumoKwh = (equipamento.consumoWatts / 1000) * horasMensais;
    return consumoKwh * parseFloat(precoKwh);
  };

  const totalConsumo = equipamentos.reduce(
    (totais, equipamento) => {
      const horasMensais =
        equipamento.periodicidade === "dia"
          ? equipamento.horas * 30
          : equipamento.periodicidade === "semana"
            ? equipamento.horas * 4
            : equipamento.horas;

      const consumoKwh = (equipamento.consumoWatts / 1000) * horasMensais;

      return {
        kwh: totais.kwh + consumoKwh,
        reais: totais.reais + consumoKwh * parseFloat(precoKwh),
      };
    },
    { kwh: 0, reais: 0 }
  );

  return (
    <div className="min-h-screen w-full">
      <div className="min-h-screen bg-gray-100 flex flex-col items-center p-5 py-10">
        <h1 className="text-3xl font-bold text-blue-600">Gerenciador de Consumo Eletrônico</h1>
        <p className="text-gray-700 mt-4">Adicione ou edite equipamentos eletrônicos e calcule o custo mensal.</p>
        <div className="mt-6 bg-white p-6 rounded shadow-lg w-full max-w-lg">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Preço do kWh (R$)</label>
            <InputNumber
              placeholder="1.00000000"
              value={precoKwh.toString()}
              onChange={(value) => salvarPrecoKwh(value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-10 bg-white p-6 rounded shadow-lg w-full max-w-lg">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Nome do Equipamento</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4 flex items-center">
            <div className="w-2/3">
              <label className="block text-sm font-medium text-gray-700">Horas</label>
              <InputNumber
                placeholder="24"
                value={horas}
                onChange={(value) => setHoras(value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div className="w-1/3 ml-4">
              <label className="block text-sm font-medium text-gray-700">Periodicidade</label>
              <select
                value={periodicidade}
                onChange={(e) => setPeriodicidade(e.target.value as "dia" | "semana" | "mes")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="dia">Por Dia</option>
                <option value="semana">Por Semana</option>
                <option value="mes">Por Mês</option>
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Consumo em Watts</label>
            <InputNumber
              placeholder="100"
              value={consumoWatts}
              onChange={(value) => setConsumoWatts(value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={salvarEquipamento}
            className={`w-full ${editIndex !== null ? "bg-green-600" : "bg-blue-600"} text-white py-2 rounded-md shadow hover:opacity-90 transition`}
          >
            {editIndex !== null ? "Salvar Alterações" : "Salvar Equipamento"}
          </button>
        </div>

        <div className="mt-10 w-full max-w-lg">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Total: {totalConsumo.kwh.toFixed(2)} kWh/mês - R$ {totalConsumo.reais.toFixed(2)}
          </h2>
          <ul className="space-y-2">
            {equipamentos.map((equipamento, index) => (
              <li
                key={index}
                className="bg-gray-50 border rounded p-4 flex justify-between items-center"
              >
                <span className="text-gray-700">{equipamento.nome}</span>
                <span className="text-gray-500 text-sm">
                  {equipamento.horas}h/{equipamento.periodicidade}
                </span>
                <span className="text-gray-500 text-sm">{equipamento.consumoWatts}W</span>
                <span className="text-gray-800 font-semibold">
                  R$ {calcularCustoMensal(equipamento).toFixed(2)}
                </span>
                <div className="grid space-x-2">
                  <button
                    onClick={() => editarEquipamento(index)}
                    className="text-blue-600 hover:underline"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => excluirEquipamento(index)}
                    className="text-red-600 hover:underline"
                  >
                    Excluir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default App;
